import type { UniversalWorkDetail } from "@excellent/core/verification-universal-surfaces";
import { useApi } from "@excellent/ui/api";
import {
  WORK_CHAIN_ID_PATTERN,
  verificationWorkChain,
} from "@excellent/ui/api/contracts/verification-work-chain";

import { verificationCanonicalClients } from "../../api/canonical-clients";
import { useCanonicalEnvironmentOptional } from "../../canonical-environment-provider";
import { href } from "../../nav";
import {
  canonicalWorkHref,
  canonicalWorkReadQuery,
  canonicalWorkResponseMatches,
} from "../work/environment-read-scope";
import { resolveReceiptVerdicts, verdictFrom } from "../../resolved-verdicts";
import {
  ActionButton,
  Callout,
  Field,
  Fields,
  Mono,
  ScopeTabs,
  Section,
  Stat,
  StatGrid,
  ToneBadge,
  Verdict,
} from "../../ui";
import { PageError, PageFrame, PagePending, type VerificationDetailPageProps } from "../page-frame";
import { WorkChainView } from "./chain";
import { WorkJourney } from "./journey";
import {
  canInspectReceiptEvidence,
  historyRecordCount,
  resolveWorkDetailRequest,
  workDetailErrorState,
} from "./model";

type WorkDetailTab = "chain" | "summary" | "current" | "history" | "evidence";
type Contract = UniversalWorkDetail["incomplete"]["contracts"][number];
type Attempt = UniversalWorkDetail["incomplete"]["attempts"][number];
type Receipt = UniversalWorkDetail["incomplete"]["receipts"][number];
type FirstUnknownReview = UniversalWorkDetail["firstUnknownReviews"][number];
type RecurrenceDriftReview = UniversalWorkDetail["recurrenceDriftReviews"][number];
type HistoryBucket = UniversalWorkDetail["incomplete"];

function readTab(): WorkDetailTab {
  if (typeof window === "undefined") return "chain";
  const tab = new URLSearchParams(window.location.search).get("tab");
  if (tab === "current" || tab === "history" || tab === "evidence" || tab === "summary") return tab;
  return "chain";
}

function detailPath(id: string, tab: WorkDetailTab): string {
  return `${href("work-ledger", id)}?tab=${tab}`;
}

function shortDigest(value: string): string {
  return value.length > 34 ? `${value.slice(0, 18)}…${value.slice(-10)}` : value;
}

function recordSummary(bucket: HistoryBucket): string {
  return [
    `${bucket.contracts.length} contract(s)`,
    `${bucket.attempts.length} attempt(s)`,
    `${bucket.receipts.length} receipt(s)`,
    `${bucket.amendments.length} amendment(s)`,
    `${bucket.reviews.length} review(s)`,
    `${bucket.learningProposals.length} learning proposal(s)`,
  ].join(" · ");
}

function ContractList({
  contracts,
  historical,
}: {
  contracts: readonly Contract[];
  historical: boolean;
}) {
  if (contracts.length === 0) {
    return (
      <Callout title="No contracts">
        No {historical ? "historical" : "current"} Contract is linked to this Work.
      </Callout>
    );
  }
  return (
    <div className="grid gap-3 lg:grid-cols-2">
      {contracts.map((contract) => (
        <div
          key={`${contract.content.contractId}:${contract.content.version}:${contract.contractDigest}`}
          className="rounded-md border border-border p-4"
          data-authority={historical ? "historical-only" : "current-candidate"}
        >
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <strong>{contract.content.title}</strong>
            <ToneBadge tone={historical ? "unknown" : "accent"}>
              {historical ? "History only" : contract.deploymentMode}
            </ToneBadge>
          </div>
          <Fields>
            <Field label="Contract">
              <Mono>{contract.content.contractId}</Mono>
            </Field>
            <Field label="Version">{contract.content.version}</Field>
            <Field label="Digest">
              <Mono>{shortDigest(contract.contractDigest)}</Mono>
            </Field>
            <Field label="Accepted">{contract.acceptance.acceptedAt}</Field>
          </Fields>
        </div>
      ))}
    </div>
  );
}

function AttemptList({
  attempts,
  historical,
}: {
  attempts: readonly Attempt[];
  historical: boolean;
}) {
  if (attempts.length === 0) {
    return (
      <Callout title="No attempts">
        No {historical ? "historical" : "current"} Attempt is linked to this Work.
      </Callout>
    );
  }
  return (
    <div className="grid gap-3 lg:grid-cols-2">
      {attempts.map((attempt) => (
        <div
          key={attempt.binding.attemptId}
          className="rounded-md border border-border p-4"
          data-authority={historical ? "historical-only" : "current-candidate"}
        >
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <Mono>{attempt.binding.attemptId}</Mono>
            <ToneBadge
              tone={historical ? "unknown" : attempt.state === "SUBMITTED" ? "pass" : "accent"}
            >
              {historical ? "History only" : attempt.state}
            </ToneBadge>
          </div>
          <Fields>
            <Field label="Worker">{attempt.worker.actorId}</Field>
            <Field label="Contract">
              <Mono>
                {attempt.binding.contractId}:{attempt.binding.contractVersion}
              </Mono>
            </Field>
            <Field label="Input">
              <Mono>{shortDigest(attempt.binding.inputSnapshotDigest)}</Mono>
            </Field>
            <Field label="Output">
              <Mono>
                {attempt.outputSnapshotDigest
                  ? shortDigest(attempt.outputSnapshotDigest)
                  : "Not submitted"}
              </Mono>
            </Field>
          </Fields>
        </div>
      ))}
    </div>
  );
}

/**
 * Exported for the gate, and for the same reason `projectReceipt` in the work-chain
 * route is: the only way to prove this list reads a PENDING receipt correctly on BOTH
 * sides of its deadline is to hand it the same receipts twice with a different `now`.
 * Driving it through `WorkDetailPage` could not do that — that component owns the
 * clock and fetches its own data.
 */
export function ReceiptList({
  detail,
  receipts,
  bucket,
  onNavigate,
  now,
}: {
  detail: UniversalWorkDetail;
  receipts: readonly Receipt[];
  bucket: "current" | "history";
  onNavigate: (to: string) => void;
  /** The instant every stored verdict on this list is read against. Injected, never ambient. */
  now: string;
}) {
  // READ TIME, once for the whole list. A signed receipt cannot be settled in place,
  // so a PENDING one keeps saying PENDING past its deadline; resolving here — rather
  // than inside the row renderer below — is also what stops two rows on one screen
  // being read against two different instants.
  const resolvedVerdicts = resolveReceiptVerdicts(receipts, now);
  if (receipts.length === 0) {
    return (
      <Callout title="No receipts">
        No {bucket === "history" ? "historical" : "current"} Receipt is linked to this Work.
      </Callout>
    );
  }
  return (
    <div className="grid gap-3 lg:grid-cols-2">
      {receipts.map((receipt) => {
        const authorized = canInspectReceiptEvidence(detail, receipt, bucket);
        return (
          <div
            key={receipt.payload.id}
            className="rounded-md border border-border p-4"
            data-authority={authorized ? "current-prospective" : "historical-only"}
          >
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <Mono>{receipt.payload.id}</Mono>
              <div className="flex flex-wrap gap-2">
                <Verdict status={verdictFrom(resolvedVerdicts, receipt, now)} />
                <ToneBadge tone={authorized ? "neutral" : "unknown"}>
                  {authorized ? "Evidence available" : "History only"}
                </ToneBadge>
              </div>
            </div>
            <Fields>
              <Field label="Assurance">{receipt.payload.assurance}</Field>
              <Field label="Attempt">
                <Mono>{receipt.payload.binding.attemptId}</Mono>
              </Field>
              <Field label="Contract">
                <Mono>
                  {receipt.payload.binding.contractId}:{receipt.payload.binding.contractVersion}
                </Mono>
              </Field>
              <Field label="Issued">{receipt.payload.issuedAt}</Field>
            </Fields>
            {authorized ? (
              <div className="mt-3">
                <ActionButton
                  actionId="work-detail.inspect-current-receipt-evidence"
                  onClick={() => onNavigate(detailPath(detail.work.id, "evidence"))}
                >
                  Inspect bound evidence
                </ActionButton>
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function SupportingHistory({ bucket, historical }: { bucket: HistoryBucket; historical: boolean }) {
  const events = [
    ...bucket.amendments.map(({ proposal, decision }) => ({
      id: proposal.id,
      kind: "Amendment",
      state: decision?.decision ?? "PENDING",
      at: decision?.decidedAt ?? proposal.proposedAt,
    })),
    ...bucket.reviews.map(({ request, resolution }) => ({
      id: request.id,
      kind: "Review",
      state: resolution?.outcome ?? request.state,
      at: resolution?.resolvedAt ?? request.requestedAt,
    })),
    ...bucket.learningProposals.map((proposal) => ({
      id: proposal.id,
      kind: "Learning proposal",
      state: proposal.state,
      at: proposal.proposedAt,
    })),
  ];
  if (events.length === 0) return null;
  return (
    <Section
      title="Amendment, review, and learning history"
      note={
        historical
          ? "These records are readable history only and never create current completion authority."
          : "Current-chain review context from the server projection."
      }
    >
      <div className="grid gap-2">
        {events.map((event) => (
          <div
            key={`${event.kind}:${event.id}`}
            className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border p-3"
          >
            <div>
              <strong>{event.kind}</strong> · <Mono>{event.id}</Mono>
            </div>
            <div className="flex items-center gap-2">
              <ToneBadge tone={historical ? "unknown" : "accent"}>
                {historical ? "History only" : event.state}
              </ToneBadge>
              <span className="type-subtext text-muted-foreground">{event.at}</span>
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}

function FirstUnknownReviews({ reviews }: { reviews: readonly FirstUnknownReview[] }) {
  if (reviews.length === 0) return null;
  return (
    <Section
      title="First UNKNOWN confirmation"
      note="Customer-facing first-run review. This is not a canonical verdict and cannot authorize completion."
    >
      <div className="grid gap-3">
        {reviews.map((review) => (
          <div
            key={review.customerView.reviewId}
            className="rounded-md border border-border p-4"
            data-authority="historical-only"
            data-first-unknown-review={review.customerView.reviewId}
            data-canonical-verdict-hidden={String(review.customerView.canonicalVerdictHidden)}
            data-eligibility={review.customerView.eligibilityLabel}
          >
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <div>
                <strong>First UNKNOWN confirmation</strong> ·{" "}
                <Mono>{review.customerView.occurrenceId}</Mono>
              </div>
              <div className="flex flex-wrap gap-2">
                <ToneBadge tone="unknown">{review.customerView.statusLabel}</ToneBadge>
                <ToneBadge tone="unknown">{review.customerView.eligibilityLabel}</ToneBadge>
                <ToneBadge tone="unknown">History only</ToneBadge>
              </div>
            </div>
            <Fields>
              <Field label="Candidate digest">
                <Mono>{shortDigest(review.customerView.candidateDigest)}</Mono>
              </Field>
              <Field label="Canonical verdict">
                {review.customerView.canonicalVerdictHidden ? "Hidden until accepted" : "Visible"}
              </Field>
              <Field label="Workflow textarea">
                {review.customerView.workflowTextareaRequired ? "Required" : "Not required"}
              </Field>
              <Field label="Process">
                <Mono>{review.processId}</Mono>
              </Field>
            </Fields>
            <div className="mt-4 grid gap-3">
              {review.customerView.cards.map((card) => (
                <div
                  key={card.id}
                  className="rounded-md border border-border bg-muted/20 p-3"
                  data-first-unknown-card={card.id}
                >
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <strong>{card.whatHappened}</strong>
                    <ToneBadge tone={card.requiresPolicyAuthorityToDrop ? "fail" : "unknown"}>
                      {card.requiresPolicyAuthorityToDrop ? "Policy authority to drop" : "Optional"}
                    </ToneBadge>
                  </div>
                  <Fields>
                    <Field label="Will check">{card.willCheck}</Field>
                    <Field label="Why">{card.why}</Field>
                    <Field label="Evidence oracles">{card.evidenceOracles.join(", ")}</Field>
                    <Field label="Blind spots">{card.blindSpots.join(", ")}</Field>
                    <Field label="Drop warning">{card.dropWarning}</Field>
                  </Fields>
                </div>
              ))}
            </div>
            <div
              className="mt-4 flex flex-wrap gap-2"
              data-first-unknown-actions={review.customerView.actions.join(",")}
            >
              {review.customerView.actions.map((action) => (
                <ActionButton
                  key={action}
                  actionId={`work-detail.first-unknown.${action}`}
                  disabled
                  reason="First-unknown review actions are read-only on this surface."
                  size="sm"
                >
                  {action.replaceAll("_", " ")}
                </ActionButton>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}

function recurrenceTone(relationship: RecurrenceDriftReview["recurrence"]["relationship"]) {
  return relationship === "same" ? "pass" : relationship === "possible" ? "accent" : "fail";
}

function RecurrenceDriftReviews({ reviews }: { reviews: readonly RecurrenceDriftReview[] }) {
  if (reviews.length === 0) return null;
  return (
    <Section
      title="Recurrence and drift review"
      note="Same, possible, and different recurrence evidence is visible here, but this history projection is non-authoritative until the accepted contract or successor is separately accepted."
    >
      <div className="grid gap-3">
        {reviews.map((review) => (
          <div
            key={`${review.recurrence.id}:${review.application.id}`}
            className="rounded-md border border-border p-4"
            data-authority="historical-only"
            data-recurrence-relationship={review.recurrence.relationship}
            data-drift-relationship={review.driftAssessment.relationship}
            data-application-state={review.application.state}
            data-protected-execution={review.protectedExecutionActionState}
            data-successor-state={review.application.successorProposal?.state ?? "none"}
          >
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <div>
                <strong>Recurrence review</strong> · <Mono>{review.recurrence.occurrenceId}</Mono>
              </div>
              <div className="flex flex-wrap gap-2">
                <ToneBadge tone={recurrenceTone(review.recurrence.relationship)}>
                  {review.customerCopy.recurrenceLabel}
                </ToneBadge>
                <ToneBadge
                  tone={
                    review.application.canonicalVerdictState === "ELIGIBLE" ? "pass" : "unknown"
                  }
                >
                  {review.customerCopy.verdictLabel}
                </ToneBadge>
                <ToneBadge tone="unknown">{review.customerCopy.authorityLabel}</ToneBadge>
              </div>
            </div>
            <Fields>
              <Field label="Process">
                <Mono>{review.processId}</Mono>
              </Field>
              <Field label="Base fingerprint">
                <Mono>{shortDigest(review.recurrence.baseFingerprintDigest)}</Mono>
              </Field>
              <Field label="Observed fingerprint">
                <Mono>{shortDigest(review.recurrence.observedFingerprintDigest)}</Mono>
              </Field>
              <Field label="Drift signals">
                {review.recurrence.driftSignals.length
                  ? review.recurrence.driftSignals.join(", ")
                  : "none"}
              </Field>
              <Field label="Changed dimensions">
                {review.driftAssessment.changedDimensions.length
                  ? review.driftAssessment.changedDimensions.join(", ")
                  : "none"}
              </Field>
              <Field label="Historical contract">
                <Mono>{shortDigest(review.application.historicalAttemptContractDigest)}</Mono>
              </Field>
              <Field label="Attached contract">
                {review.application.attachedContractDigest ? (
                  <Mono>{shortDigest(review.application.attachedContractDigest)}</Mono>
                ) : (
                  "none before confirmation"
                )}
              </Field>
              <Field label="Policy review">
                {review.driftAssessment.policyReviewRequired ? "required" : "not required"}
              </Field>
              <Field label="Successor proposal">
                {review.application.successorProposal ? (
                  <>
                    <Mono>{review.application.successorProposal.id}</Mono> ·{" "}
                    {review.application.successorProposal.state} · returns to{" "}
                    {review.application.successorProposal.returnsOccurrenceTo}
                  </>
                ) : (
                  review.customerCopy.successorLabel
                )}
              </Field>
              <Field label="Protected execution">
                {review.customerCopy.protectedExecutionLabel}
              </Field>
            </Fields>
            <div className="mt-4 flex flex-wrap gap-2">
              <ActionButton
                actionId="work-detail.recurrence.confirm"
                disabled
                reason="Recurrence confirmation is read-only on this surface."
                size="sm"
              >
                confirm recurrence
              </ActionButton>
              <ActionButton
                actionId="work-detail.recurrence.accept-successor"
                disabled
                reason="Successor acceptance is read-only on this surface."
                size="sm"
              >
                accept successor before protected execution
              </ActionButton>
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}

function CurrentView({
  detail,
  onNavigate,
  now,
}: {
  detail: UniversalWorkDetail;
  onNavigate: (to: string) => void;
  now: string;
}) {
  return (
    <div className="flex flex-col gap-5" data-work-detail-bucket="current">
      <Callout
        tone={
          detail.actionAuthority.completionAuthority === "CURRENT_PROSPECTIVE" ? "pass" : "unknown"
        }
        title="Server-issued action authority"
      >
        {detail.actionAuthority.completionAuthority === "CURRENT_PROSPECTIVE"
          ? "Only records matching the exact server-returned Contract id, version, digest, Attempt id, and Receipt id expose an action."
          : "No current prospective Receipt authorizes a completion action."}
      </Callout>
      <Section title="Current contracts">
        <ContractList contracts={detail.current.contracts} historical={false} />
      </Section>
      <Section title="Attempts under the current contract">
        <AttemptList attempts={detail.current.attempts} historical={false} />
      </Section>
      <Section
        title="Recorded results under the current contract"
        note="Includes earlier failed checks. Evidence access does not mean that a result authorizes completion; follow the latest result in the repair history above."
      >
        <ReceiptList
          detail={detail}
          receipts={detail.current.receipts}
          bucket="current"
          onNavigate={onNavigate}
          now={now}
        />
      </Section>
      <SupportingHistory bucket={detail.current} historical={false} />
    </div>
  );
}

function HistoryView({
  detail,
  onNavigate,
  now,
}: {
  detail: UniversalWorkDetail;
  onNavigate: (to: string) => void;
  now: string;
}) {
  return (
    <div
      className="flex flex-col gap-5"
      data-work-detail-bucket="history"
      data-authority="historical-only"
    >
      <Callout tone="unknown" title="History only — non-authoritative">
        Retrospective, superseded, legacy, and incomplete records remain visible for audit. They
        cannot authorize completion or expose Work actions. Earlier attempts and results under the
        current contract appear in the attempt and repair history above, including when this archive
        is empty.
      </Callout>
      <Section title="Historical contracts">
        <ContractList contracts={detail.incomplete.contracts} historical />
      </Section>
      <Section title="Historical attempts">
        <AttemptList attempts={detail.incomplete.attempts} historical />
      </Section>
      <Section title="Historical receipts">
        <ReceiptList
          detail={detail}
          receipts={detail.incomplete.receipts}
          bucket="history"
          onNavigate={onNavigate}
          now={now}
        />
      </Section>
      <FirstUnknownReviews reviews={detail.firstUnknownReviews} />
      <RecurrenceDriftReviews reviews={detail.recurrenceDriftReviews} />
      <SupportingHistory bucket={detail.incomplete} historical />
    </div>
  );
}

function EvidenceView({ detail }: { detail: UniversalWorkDetail }) {
  return (
    <div className="flex flex-col gap-5">
      <Section
        title="Admitted evidence"
        note="Every bundle is bound by the API contract to an exact Receipt ancestry returned for this Work."
      >
        {detail.evidence.length ? (
          <div className="grid gap-3 lg:grid-cols-2">
            {detail.evidence.map((bundle) => (
              <div key={bundle.submissionDigest} className="rounded-md border border-border p-4">
                <Fields>
                  <Field label="Attempt binding">
                    <Mono>{bundle.binding.attemptId}</Mono>
                  </Field>
                  <Field label="Bundle digest">
                    <Mono>{shortDigest(bundle.submissionDigest)}</Mono>
                  </Field>
                  <Field label="Artifacts">{bundle.artifacts.length}</Field>
                  <Field label="Gaps">{bundle.submission.gaps.length}</Field>
                  <Field label="Admitted by">{bundle.admittedBy.actorId}</Field>
                  <Field label="Admitted at">{bundle.admittedAt}</Field>
                </Fields>
              </div>
            ))}
          </div>
        ) : (
          <Callout title="No admitted evidence">
            No evidence bundle is linked through a returned Receipt.
          </Callout>
        )}
      </Section>
      <Section
        title="Enforcement effects"
        note="Effects are displayed only with the Receipt id supplied by the universal Work-detail response."
      >
        {detail.effects.length ? (
          <div className="grid gap-3 lg:grid-cols-2">
            {detail.effects.map(({ receiptId, receiptPayloadDigest, decision, effect }) => (
              <div
                key={`${receiptId}:${effect.id}`}
                className="rounded-md border border-border p-4"
              >
                <Fields>
                  <Field label="Receipt">
                    <Mono>{receiptId}</Mono>
                  </Field>
                  <Field label="Receipt payload">
                    <Mono>{shortDigest(receiptPayloadDigest)}</Mono>
                  </Field>
                  <Field label="Decision">
                    <Mono>{decision.id}</Mono> · {decision.disposition}
                  </Field>
                  <Field label="Effect">
                    <Mono>{effect.id}</Mono>
                  </Field>
                  <Field label="State">
                    <ToneBadge
                      tone={
                        effect.state === "APPLIED"
                          ? "pass"
                          : effect.state === "FAILED"
                            ? "fail"
                            : "unknown"
                      }
                    >
                      {effect.state}
                    </ToneBadge>
                  </Field>
                  <Field label="Connector">{effect.connectorRef}</Field>
                  <Field label="External ref">{effect.externalRef ?? "None"}</Field>
                </Fields>
              </div>
            ))}
          </div>
        ) : (
          <Callout title="No effects">
            No enforcement effect is linked to a returned Receipt.
          </Callout>
        )}
      </Section>
    </div>
  );
}

function SummaryView({ detail }: { detail: UniversalWorkDetail }) {
  return (
    <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(19rem,0.48fr)]">
      <Section
        title="Work"
        note="Canonical tenant-bound Work identity; no demo or real-environment translation is applied."
      >
        <Fields>
          <Field label="Work id">
            <Mono>{detail.work.id}</Mono>
          </Field>
          <Field label="Tenant">
            <Mono>{detail.work.tenantId}</Mono>
          </Field>
          <Field label="Project">{detail.work.projectId ?? "None"}</Field>
          <Field label="Type">{detail.work.workType}</Field>
          <Field label="State">{detail.work.state}</Field>
          <Field label="Consequence">{detail.work.consequence}</Field>
          <Field label="Requested by">{detail.work.requestedBy.actorId}</Field>
          <Field label="Source">{detail.work.source.uri}</Field>
          <Field label="Updated">{detail.work.updatedAt}</Field>
        </Fields>
      </Section>
      <Section
        title="Authority split"
        note="The server separates current prospective authority from readable history."
      >
        <div className="flex flex-col gap-3">
          <ToneBadge
            tone={
              detail.actionAuthority.completionAuthority === "CURRENT_PROSPECTIVE"
                ? "pass"
                : "unknown"
            }
            size="lg"
          >
            {detail.actionAuthority.completionAuthority}
          </ToneBadge>
          <Callout title="Current chain">{recordSummary(detail.current)}</Callout>
          <Callout tone="unknown" title="History only">
            {recordSummary(detail.incomplete)}
          </Callout>
        </div>
      </Section>
    </div>
  );
}

export function WorkDetailPage({
  id,
  onNavigate,
  now = new Date().toISOString(),
}: VerificationDetailPageProps & {
  /**
   * THE RENDER EDGE. The one clock read for this page, passed down to every list that
   * renders a stored verdict. A `new Date()` deeper in the tree would make the page
   * untestable and would let two sections disagree about what time it is.
   */
  now?: string;
}) {
  const request = resolveWorkDetailRequest(id);
  const tab = readTab();
  const canonicalContext = useCanonicalEnvironmentOptional();
  const workQuery = canonicalWorkReadQuery(canonicalContext);
  const navigateWithinEnvironment = (to: string) =>
    onNavigate(canonicalWorkHref(to, canonicalContext));
  const workDetail = useApi(
    verificationCanonicalClients.workDetail.operation,
    request.kind === "ready"
      ? workQuery
        ? { params: request.params, query: workQuery }
        : null
      : null,
  );
  // The universal Work-detail projection is addressed by a `work:<uuid>`
  // grammar, but the canonical Work ids a tenant actually stores are structured
  // (`work:coding:universal-canary:<digest>`). For those the chain surface is
  // the only one with an answer, so it is fetched on its own id grammar and
  // never gated behind the uuid check.
  const chain = useApi(
    verificationWorkChain.chain,
    workQuery && WORK_CHAIN_ID_PATTERN.test(id) ? { params: { id } } : null,
  );

  if (!workQuery) {
    return (
      <PageFrame page="work-detail" title="Canonical Environment context required">
        <Callout tone="unknown" title="Work detail is unavailable for this Environment">
          Work reads require one verified local Environment. Remote, offline, ambiguous, and expired
          selections fail closed until their canonical Work stores are supported.
        </Callout>
      </PageFrame>
    );
  }

  if (request.kind === "invalid") {
    if (chain.isLoading) {
      return (
        <PageFrame page="work-detail" title="Work chain">
          <PagePending title="Loading the obligation chain">
            Resolving which obligation this Work stopped at.
          </PagePending>
        </PageFrame>
      );
    }
    if (chain.data) {
      return (
        <PageFrame
          page="work-detail"
          title={chain.data.work.title}
          lede={chain.data.work.description}
          wide
        >
          <WorkChainView chain={chain.data} />
        </PageFrame>
      );
    }
    if (chain.error) {
      const chainState = workDetailErrorState(chain.error);
      return (
        <PageFrame page="work-detail" title={chainState.title}>
          <PageError
            title={chainState.title}
            error={chainState.message}
            onRetry={() => void chain.mutate()}
            actionId="work-detail.retry-chain"
          />
        </PageFrame>
      );
    }
    return (
      <PageFrame page="work-detail" title={request.title}>
        <PagePending title={request.title}>{request.message}</PagePending>
      </PageFrame>
    );
  }
  if (workDetail.isLoading) {
    return (
      <PageFrame page="work-detail" title="Work detail">
        <PagePending title="Loading Work detail">
          Resolving one canonical tenant-bound Work projection.
        </PagePending>
      </PageFrame>
    );
  }
  const responseCurrent = canonicalWorkResponseMatches(workDetail.data, workQuery);
  if (workDetail.error || (workDetail.data && !responseCurrent)) {
    const state = workDetail.error
      ? workDetailErrorState(workDetail.error)
      : workDetailErrorState(new Error("Work response did not match the selected Environment."));
    // A refused universal projection must not also hide where the Work stopped.
    if (chain.data) {
      return (
        <PageFrame
          page="work-detail"
          title={chain.data.work.title}
          lede={chain.data.work.description}
          wide
        >
          <Callout tone="unknown" title={state.title}>
            {state.message} The obligation chain below is read from the stored protocol records and
            is unaffected.
          </Callout>
          <WorkChainView chain={chain.data} />
        </PageFrame>
      );
    }
    return (
      <PageFrame page="work-detail" title={state.title}>
        <PageError
          title={state.title}
          error={state.message}
          onRetry={() => void workDetail.mutate()}
          actionId="work-detail.retry"
        />
      </PageFrame>
    );
  }
  if (!workDetail.data || !responseCurrent) {
    return (
      <PageFrame page="work-detail" title="Work not found">
        <PagePending title="Work not found">
          No Work is available for this identifier in the current tenant.
        </PagePending>
      </PageFrame>
    );
  }

  const detail = workDetail.data;
  return (
    <PageFrame
      page="work-detail"
      title={detail.work.title}
      lede={detail.work.description}
      actions={
        <ScopeTabs
          value={tab}
          actionId="work-detail.tab"
          onChange={(next) => navigateWithinEnvironment(detailPath(detail.work.id, next))}
          options={[
            { key: "chain", label: "Chain", actionId: "work-detail.tab-chain" },
            { key: "summary", label: "Summary", actionId: "work-detail.tab-summary" },
            { key: "current", label: "Current", actionId: "work-detail.tab-current" },
            { key: "history", label: "History", actionId: "work-detail.tab-history" },
            { key: "evidence", label: "Evidence & effects", actionId: "work-detail.tab-evidence" },
          ]}
        />
      }
      wide
    >
      <WorkJourney
        detail={detail}
        now={now}
        onRefresh={async () => {
          const results = await Promise.allSettled([workDetail.mutate(), chain.mutate()]);
          if (results.some((result) => result.status === "rejected"))
            throw new Error("Work refresh failed");
        }}
      />
      <StatGrid cols={4}>
        <Stat
          label="Work state"
          value={detail.work.state}
          tone={detail.work.state === "CLOSED" ? "pass" : "accent"}
        />
        <Stat
          label="Completion authority"
          value={
            detail.actionAuthority.completionAuthority === "CURRENT_PROSPECTIVE"
              ? "CURRENT"
              : "NONE"
          }
          tone={
            detail.actionAuthority.completionAuthority === "CURRENT_PROSPECTIVE"
              ? "pass"
              : "unknown"
          }
        />
        <Stat label="Recorded receipts" value={detail.current.receipts.length} />
        <Stat
          label="Archived records"
          value={historyRecordCount(detail)}
          tone={historyRecordCount(detail) ? "unknown" : undefined}
        />
      </StatGrid>

      {tab === "chain" ? (
        chain.data ? (
          <WorkChainView chain={chain.data} />
        ) : (
          <PagePending title="Loading the obligation chain">
            Resolving which obligation this Work stopped at.
          </PagePending>
        )
      ) : null}
      {tab === "summary" ? <SummaryView detail={detail} /> : null}
      {tab === "current" ? (
        <CurrentView detail={detail} onNavigate={navigateWithinEnvironment} now={now} />
      ) : null}
      {tab === "history" ? (
        <HistoryView detail={detail} onNavigate={navigateWithinEnvironment} now={now} />
      ) : null}
      {tab === "evidence" ? <EvidenceView detail={detail} /> : null}
    </PageFrame>
  );
}
